import type { PricingBreakdown } from '@data/publicPlans';

type CopyFn = (en: string, de: string, hr: string) => string;

type PlanMaterialsQuantitiesCardProps = {
	materials: PricingBreakdown['materials'];
	copy: CopyFn;
	className?: string;
	editable?: boolean;
	isEditing?: boolean;
	onChange?: (next: PricingBreakdown['materials']) => void;
};

/** Materials scope for project plan — quantities and units only, no prices. */
export default function PlanMaterialsQuantitiesCard({
	materials,
	copy,
	className = '',
	editable = false,
	isEditing = false,
	onChange
}: PlanMaterialsQuantitiesCardProps) {
	if (editable && isEditing && onChange) {
		return (
			<div className={`card mb-4 ${className}`.trim()}>
				<div className="card-header d-flex justify-content-between align-items-center">
					<h5 className="mb-0">
						<i className="bi bi-box-seam text-success me-2" aria-hidden />
						{copy('Materials', 'Materialien', 'Materijali')}
					</h5>
					<button
						type="button"
						className="btn btn-sm btn-outline-success"
						onClick={() =>
							onChange([...materials, { name: '', quantity: 0, unit: 'pcs', unitPrice: 0, total: 0 }])
						}
					>
						+ {copy('Add', 'Hinzufügen', 'Dodaj')}
					</button>
				</div>
				<div className="card-body small">
					{materials.map((m, idx) => (
						<div key={idx} className="border rounded p-2 mb-2">
							<div className="d-flex gap-2 align-items-center mb-2">
								<input
									type="text"
									className="form-control form-control-sm"
									placeholder={copy('Material', 'Material', 'Materijal')}
									value={m.name}
									onChange={(e) => {
										const next = [...materials];
										next[idx] = { ...next[idx], name: e.target.value };
										onChange(next);
									}}
								/>
								<button
									type="button"
									className="btn btn-sm btn-outline-danger p-1"
									onClick={() => onChange(materials.filter((_, i) => i !== idx))}
								>
									<i className="bi bi-trash text-danger" aria-hidden />
								</button>
							</div>
							<div className="d-flex gap-2 align-items-center">
								<input
									type="number"
									className="form-control form-control-sm"
									style={{ maxWidth: '6rem' }}
									placeholder={copy('Quantity', 'Menge', 'Količina')}
									value={m.quantity || ''}
									onChange={(e) => {
										const next = [...materials];
										const quantity = Number(e.target.value) || 0;
										next[idx] = { ...next[idx], quantity };
										onChange(next);
									}}
								/>
								<input
									type="text"
									className="form-control form-control-sm"
									style={{ maxWidth: '7rem' }}
									placeholder={copy('Unit', 'Einheit', 'Jedinica')}
									value={m.unit}
									onChange={(e) => {
										const next = [...materials];
										next[idx] = { ...next[idx], unit: e.target.value };
										onChange(next);
									}}
								/>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}

	if (!materials.length) return null;

	return (
		<div className={`card mb-4 ${className}`.trim()}>
			<div className="card-header">
				<h5 className="mb-0">
					<i className="bi bi-box-seam text-success me-2" aria-hidden />
					{copy('Materials', 'Materialien', 'Materijali')}
				</h5>
			</div>
			<div className="card-body small p-0">
				<p className="text-muted px-3 pt-3 pb-2 mb-0 border-bottom">
					{copy(
						'Quantities for build scope — pricing is on the Detailed Plan tab.',
						'Mengen für den Bauumfang — Preise finden Sie im Tab Detaillierter Plan.',
						'Količine za opseg radova — cijene su na kartici Detaljni plan.'
					)}
				</p>
				<div className="table-responsive">
					<table className="table table-sm table-bordered table-hover align-middle mb-0">
						<thead className="table-light">
							<tr>
								<th scope="col" className="ps-3 py-2 fw-semibold text-muted small text-uppercase">
									{copy('Material', 'Material', 'Materijal')}
								</th>
								<th
									scope="col"
									className="pe-3 py-2 fw-semibold text-muted small text-uppercase text-end text-nowrap"
									style={{ width: '8.5rem' }}
								>
									{copy('Quantity', 'Menge', 'Količina')}
								</th>
							</tr>
						</thead>
						<tbody>
							{materials.map((m, idx) => (
								<tr key={idx}>
									<td className="ps-3 py-2">{m.name}</td>
									<td className="pe-3 py-2 text-end fw-bold text-success text-nowrap">
										{m.quantity} {m.unit}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}
